import { CartUpdateEvent } from '@theme/events';

/** Cart rewards never set prices. Shopify must fund each gift with an actually free matching unit. */
const SOURCE = 'para-cart-rewards';
const OWN_PROPERTY = '_para_reward';
const VERSION_PROPERTY = '_para_reward_version';
const ROOT = 'para-cart-rewards';

export function ownedTier(item) {
  const match = /^tier-([1-5])$/.exec(String(item?.properties?.[OWN_PROPERTY] || ''));
  return match && (!item.properties[VERSION_PROPERTY] || item.properties[VERSION_PROPERTY] === '1') ? match[1] : null;
}

function unitPrice(item) {
  const quantity = Number(item.quantity);
  const price = Number(item.original_line_price) / quantity;
  return quantity > 0 && Number.isFinite(price) && price >= 0 ? price : null;
}

function sameUnitKey(item) {
  const price = unitPrice(item);
  return price !== null && !item.selling_plan_allocation
    ? JSON.stringify([String(item.product_id), String(item.variant_id || item.id), price]) : null;
}

// Shopify can put the free unit on the customer's ordinary copy instead of our marked line.
// A partly discounted line only contributes free units when an explicit 100% allocation proves them.
export function freeUnits(item) {
  const quantity = Number(item.quantity);
  const price = unitPrice(item);
  if (!Number.isSafeInteger(quantity) || quantity < 1 || price === null) return 0;
  if (Number(item.final_line_price) === 0) return quantity;
  if (price === 0) return 0;
  const freeAmount = (item.line_level_discount_allocations || []).reduce((total, allocation) => {
    const application = allocation.discount_application;
    return application?.value_type === 'percentage' && Number(application.value) === 100
      ? total + Math.max(0, Number(allocation.amount) || 0) : total;
  }, 0);
  const actualDiscount = Math.max(0, Number(item.original_line_price) - Number(item.final_line_price));
  return Math.min(quantity, Math.floor(Math.min(freeAmount, actualDiscount) / price));
}

export function coveredGiftLines(cart) {
  const items = cart?.items || [];
  const credits = new Map();
  for (const item of items) {
    const key = sameUnitKey(item);
    if (!key || item.properties?.[OWN_PROPERTY]) continue;
    credits.set(key, (credits.get(key) || 0) + freeUnits(item));
  }
  const covered = new Set();
  const products = new Set();
  // Prefer a marked line already free; another marked duplicate cannot finance itself.
  const owned = items.filter((item) => ownedTier(item) && item.quantity === 1 && !item.selling_plan_allocation)
    .sort((left, right) => Number(Number(right.final_line_price) === 0) - Number(Number(left.final_line_price) === 0));
  for (const item of owned) {
    const productId = String(item.product_id);
    if (products.has(productId)) continue;
    const key = sameUnitKey(item);
    if (Number(item.final_line_price) === 0) {
      covered.add(item);
    } else if (key && (credits.get(key) || 0) > 0) {
      credits.set(key, credits.get(key) - 1);
      covered.add(item);
    } else continue;
    products.add(productId);
  }
  return covered;
}

function qualifyingLineAmount(item) {
  const original = Number(item.original_line_price);
  const final = Number(item.final_line_price);
  if (!Number.isFinite(original) || !Number.isFinite(final)) return 0;
  const discounted = (item.line_level_discount_allocations || []).filter((allocation) => Number(allocation.amount) > 0);
  if (!discounted.length) return final === original ? Math.max(0, original) : 0;
  // Keep excluding other product discounts, but count full-price units beside a 100%-off unit.
  if (discounted.some((allocation) => allocation.discount_application?.value_type !== 'percentage'
    || Number(allocation.discount_application.value) !== 100)) return 0;
  const paid = original - freeUnits(item) * unitPrice(item);
  return paid === final ? Math.max(0, paid) : 0;
}

export function eligibleAmount(cart, config) {
  const eligible = new Set((config.eligibleProductIds || []).map(String));
  const covered = coveredGiftLines(cart);
  return (cart?.items || []).reduce((total, item) => {
    // An ordinary purchase counts even if this product is also offered as a reward.
    // A paid marked line counts only when a normal matching copy carries its free unit.
    if (item.properties?.[OWN_PROPERTY] && !covered.has(item)) return total;
    if (config.hasCollectionFilter && !eligible.has(String(item.product_id))) return total;
    return total + qualifyingLineAmount(item);
  }, 0);
}

export function sortedTiers(config) {
  return (config.tiers || []).filter((tier) => tier.products?.some((product) => product.id) && Number(tier.amount) > 0)
    .map((tier) => ({ ...tier, id: String(tier.id), amount: Number(tier.amount) }))
    .sort((left, right) => left.amount - right.amount || Number(left.id) - Number(right.id));
}

export function qualifiedTiers(cart, config) {
  if (config.enabled === false || cart?.currency !== config.currency) return [];
  const amount = eligibleAmount(cart, config);
  const reached = sortedTiers(config).filter((tier) => amount >= tier.amount);
  return reached.slice(-1);
}

function entitledProduct(item, tier) {
  const product = tier?.products?.find((entry) => String(entry.id) === String(item.product_id));
  return product && item.quantity === 1 && !item.selling_plan_allocation
    && product.variants?.some((variant) => String(variant.id) === String(item.variant_id)) ? product : null;
}

export function invalidOwnedLines(cart, config) {
  const tier = qualifiedTiers(cart, config)[0];
  const covered = coveredGiftLines(cart);
  return (cart?.items || []).filter((item) => {
    if (!ownedTier(item)) return false;
    return !entitledProduct(item, tier) || !covered.has(item);
  });
}

function payableTotal(cart) {
  if (Number.isFinite(cart?.total_price)) return cart.total_price;
  return (cart?.items || []).reduce((total, item) => total + Number(item.final_line_price), 0);
}

function additionIsFree(cart, pending) {
  // A free copy which existed BEFORE the click cannot justify charging for a newly requested gift.
  return Number.isFinite(pending?.beforeTotal) && payableTotal(cart) <= pending.beforeTotal;
}

const configCache = new WeakMap();

function parseConfig(element) {
  const script = element.querySelector('[data-rewards-config]');
  if (!script) return null;
  const source = script.textContent || 'null';
  const cached = configCache.get(script);
  if (cached?.source === source) return cached.config;
  let config = null;
  try {
    config = JSON.parse(source);
  } catch { /* A subsequent section morph can replace invalid configuration. */ }
  // Keep rendered data fresh when a morph edits this script, without reparsing it on every render.
  configCache.set(script, { source, config });
  return config;
}

function baseFingerprint(cart) {
  return JSON.stringify({
    currency: cart?.currency,
    items: (cart?.items || []).filter((item) => !item.properties?.[OWN_PROPERTY])
      // Shopify can change line keys and attach zero-value Buy X get Y labels when a gift changes.
      // Neither is a new purchase or a reason to silently retry a rejected gift.
      .map((item) => [item.product_id, item.variant_id, item.quantity, item.original_line_price,
        item.selling_plan_allocation?.selling_plan?.id || null,
        Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right)),
        (item.line_level_discount_allocations || []).filter((allocation) => Number(allocation.amount) > 0)
          .map((allocation) => [Number(allocation.amount), allocation.discount_application?.type,
            allocation.discount_application?.title]).sort()]).sort(),
    discounts: cart?.discount_codes,
  });
}

function cartSnapshot(cart) {
  if (!Array.isArray(cart?.items) || cart.items.some((item) =>
    !Number.isFinite(item.quantity) || !Number.isFinite(item.final_line_price))) return null;
  // Common fields in Liquid's cart JSON and the Ajax cart, without volatile line keys.
  return JSON.stringify(cart.items.map((item) => [String(item.variant_id || item.id), item.quantity,
    item.final_line_price, Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right))
  ]).sort());
}

class RewardsController {
  config = null;
  cart = null;
  sections = {};
  queue = Promise.resolve();
  jobs = 0;
  revision = 0;
  syncQueued = false;
  unsafe = false;
  error = '';
  giftErrors = new Map();
  selections = new Map();
  fingerprint = '';
  pendingNative = false;
  externalMutation = null;
  openCartOnPublish = undefined;
  nativeTimer = null;
  registering = false;
  registrationUpdate = false;
  checkoutLocks = new Map();
  cartControlLocks = new Map();
  pendingPublish = false;
  uncertainMutationSnapshot = null;
  pendingAddition = null;
  nativeSections = null;

  constructor() {
    document.addEventListener('cart:update', (event) => this.externalUpdate(event));
    document.addEventListener('discount:update', (event) => this.externalUpdate(event));
    document.addEventListener('cart:error', () => this.endNative());
    document.addEventListener('para:cart-mutation:start', (event) => this.beginExternalMutation(event));
    document.addEventListener('para:cart-mutation:end', (event) => {
      if (this.externalMutation === event.detail?.token) this.endNative();
    });
    document.addEventListener('quantity-selector:update', (event) => {
      if (event.target instanceof Element && event.target.closest('cart-items-component')) {
        if (this.jobs && !this.pendingNative) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        this.beginNative();
      }
    }, true);
    document.addEventListener('click', (event) => this.handleClick(event), true);
    document.addEventListener('change', (event) => this.handleChange(event));
    document.addEventListener('submit', (event) => this.handleSubmit(event), true);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this.roots().length) this.scheduleSync();
    });
    document.addEventListener('shopify:section:load', () => this.register());
  }

  roots() {
    return [...document.querySelectorAll(ROOT)];
  }

  designMode() {
    return Boolean(window.Shopify?.designMode || this.config?.designMode);
  }

  register(domUpdated = false) {
    this.registrationUpdate ||= domUpdated;
    if (this.registering) return;
    this.registering = true;
    queueMicrotask(() => {
      this.registering = false;
      const checkCart = this.registrationUpdate;
      this.registrationUpdate = false;
      const configs = this.roots().map(parseConfig).filter(Boolean);
      const config = configs[0];
      if (!config) return;
      const changed = this.config && this.settingsFingerprint(config) !== this.settingsFingerprint(this.config);
      const currentCart = checkCart ? cartSnapshot(this.cart) : null;
      const staleCart = currentCart !== null && configs.some((entry) => {
        const renderedCart = cartSnapshot(entry.cart);
        return renderedCart !== null && renderedCart !== currentCart;
      });
      this.config = config;
      // A settings edit during an active read must be verified by a subsequent read.
      // A native cart morph alone is already covered by the synchronization in flight.
      if (changed && this.syncQueued?.started) this.revision += 1;
      if (!this.cart && !this.designMode() && (config.cart?.items || []).some(ownedTier)) this.unsafe = true;
      if (this.designMode()) {
        this.cart = config.cart || { items: [], currency: config.currency };
        this.render();
      } else if (!this.cart || changed || staleCart) {
        // A delayed native section response can restore pre-gift rows after our own update.
        // Read the server again and republish the whole cart, rather than only repairing buttons.
        if (staleCart) this.pendingPublish = true;
        this.scheduleSync();
      } else {
        this.render();
      }
    });
  }

  settingsFingerprint(config) {
    return JSON.stringify([config.enabled, config.currency, config.hasCollectionFilter,
      config.rewardProductIds, config.tiers]);
  }

  text(key, values = {}) {
    let message = this.config?.strings?.[key] || ({
      locked: 'Locked', available: 'Available', added: 'Added', choose: 'Add gift', remove: 'Remove gift',
      variantLabel: 'Option for {reward}', changeGift: 'Update gift',
      addRejected: 'This gift could not be added for free. The addition was cancelled.',
      swapFailed: 'The new option could not be added. Please select a gift again.',
      nextReward: '{amount} away from {reward}', allUnlocked: 'All rewards unlocked', unavailable: 'Unavailable',
      notFree: 'A gift was no longer eligible and was removed.',
      networkError: 'Unable to update rewards. Please try again.', busy: 'Updating your rewards…',
      currencyUnavailable: 'Rewards are unavailable in this currency.', selectVariant: 'Choose an option',
      cartSyncError: 'A gift needs to be removed before checkout. Please retry, or remove it from your cart.',
      superseded: 'Higher reward unlocked', changed: 'Your rewards have been updated.',
    })[key] || '';
    for (const [name, value] of Object.entries(values)) message = message.split(`{${name}}`).join(String(value));
    return message;
  }

  money(amount) {
    try {
      return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', {
        style: 'currency', currency: this.cart?.currency || this.config.currency,
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${this.config.currency}`;
    }
  }

  beginExternalMutation(event) {
    if (!this.config || this.designMode()) return;
    if (this.jobs || this.pendingNative || document.querySelector('cart-items-component.cart-items-disabled')) {
      event.preventDefault();
      return;
    }
    this.externalMutation = event.detail?.token;
    // Covers the look block's POST (25 s), cart read (15 s), and theme update.
    this.beginNative(60000, event.detail?.openCart);
  }

  beginNative(timeout = 15000, openCart = undefined) {
    if (!this.config || this.designMode()) return;
    this.nativeSections = null;
    this.pendingNative = true;
    this.openCartOnPublish = openCart;
    if ((this.cart?.items || this.config?.cart?.items || []).some(ownedTier)) this.unsafe = true;
    this.revision += 1;
    clearTimeout(this.nativeTimer);
    // Recover from the theme's network-error path, which does not emit cart:error.
    this.nativeTimer = setTimeout(() => this.endNative(), timeout);
    this.render();
  }

  endNative() {
    if (!this.pendingNative) return;
    this.pendingNative = false;
    this.externalMutation = null;
    clearTimeout(this.nativeTimer);
    this.scheduleSync();
  }

  externalUpdate(event) {
    if (event.detail?.sourceId === SOURCE || event.detail?.data?.source === SOURCE) return;
    // A look addition owns its lock until its finally block, including a failed cart read.
    if (!this.externalMutation) {
      this.pendingNative = false;
      clearTimeout(this.nativeTimer);
    }
    this.openCartOnPublish = event.detail?.data?.openCart;
    if ((this.cart?.items || this.config?.cart?.items || []).some(ownedTier)
      || (event.detail?.resource?.items || []).some(ownedTier)) this.unsafe = true;
    this.revision += 1;
    const sections = event.detail?.data?.sections || event.detail?.resource?.sections;
    // Reuse only the sections returned by this exact native mutation, never a previous cart state.
    this.nativeSections = sections && typeof sections === 'object'
      ? { revision: this.revision, sections } : null;
    if (this.config && !this.designMode() && !this.externalMutation) this.scheduleSync();
  }

  enqueue(operation) {
    this.jobs += 1;
    this.render();
    this.queue = this.queue.then(async () => {
      // Native quantity updates are debounced; wait for their completion event first.
      const started = Date.now();
      while ((this.pendingNative || document.querySelector('cart-items-component.cart-items-disabled'))
        && Date.now() - started < 16000) {
        await new Promise((resolve) => setTimeout(resolve, 60));
      }
      if (this.pendingNative || document.querySelector('cart-items-component.cart-items-disabled')) {
        throw new Error('Native cart operation is still pending');
      }
      return operation();
    }).catch((error) => {
      console.warn('[Cart rewards] Could not synchronize the cart.', error);
      this.error = this.text(this.unsafe ? 'cartSyncError' : 'networkError');
    }).finally(() => {
      this.jobs -= 1;
      this.render();
    });
    return this.queue;
  }

  scheduleSync() {
    if (this.syncQueued || !this.config || this.designMode()) return;
    const token = { revision: this.revision, started: false };
    this.syncQueued = token;
    this.enqueue(async () => {
      token.started = true;
      token.revision = this.revision;
      const sections = this.nativeSections?.revision === token.revision ? this.nativeSections.sections : {};
      this.nativeSections = null;
      if (!await this.loadState(sections)) return;
      if (!await this.reconcile()) return;
      this.publish();
    }).finally(() => {
      if (this.syncQueued !== token) return;
      this.syncQueued = false;
      // Native section morphs can request the same synchronization while it is still running.
      // Coalesce them; run again only if an actual cart/settings revision arrived meanwhile.
      if (token.revision !== this.revision) this.scheduleSync();
    });
  }

  async request(url, body, { format = 'json', readAttempts = 2 } = {}) {
    // Only reads may be retried. A failed POST can already have changed the cart.
    const attempts = body ? 1 : readAttempts;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: format === 'text' ? 'text/html' : 'application/json',
            ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}),
        });
        if (!response.ok) {
          const error = new Error('Cart request failed');
          error.status = response.status;
          throw error;
        }
        const data = format === 'text' ? await response.text() : await response.json();
        if (format === 'json' && (!data || data.status >= 400 || data.errors)) {
          const error = new Error('Invalid cart response');
          error.status = Number(data?.status) || response.status;
          throw error;
        }
        return data;
      } catch (error) {
        const retryable = !error.status || error.status >= 500 || error.status === 408 || error.status === 429;
        if (attempt + 1 >= attempts || !retryable) throw error;
      } finally {
        clearTimeout(timer);
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }

  route(path) {
    const root = this.config.rootUrl || window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path}`;
  }

  sectionIds() {
    return [...new Set([
      ...this.roots().map((root) => parseConfig(root)?.sectionId),
      ...[...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId),
    ].filter(Boolean))];
  }

  async loadState(bundledSections = {}) {
    const revision = this.revision;
    const sections = {};
    const ids = this.sectionIds();
    const rewardsSections = new Set(this.roots().map((root) => parseConfig(root)?.sectionId).filter(Boolean));
    const parsedSections = new Map();
    const sectionConfigs = (html) => {
      if (typeof html !== 'string') return [];
      if (!parsedSections.has(html)) {
        parsedSections.set(html,
          [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll(ROOT)].map(parseConfig).filter(Boolean));
      }
      return parsedSections.get(html);
    };
    const validSection = (id, html) => typeof html === 'string' && html.trim()
      && (this.config.enabled === false || !rewardsSections.has(id)
        || sectionConfigs(html).some((config) => config.sectionId === id));
    for (const id of ids) {
      if (validSection(id, bundledSections?.[id])) sections[id] = bundledSections[id];
    }
    const missingIds = ids.filter((id) => !sections[id]);
    for (let offset = 0; offset < missingIds.length; offset += 5) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', missingIds.slice(offset, offset + 5).join(','));
      // Shopify may return HTTP 200 with null sections, or a temporary rendering error.
      // Recover through the single-section endpoint below instead of blocking every gift click.
      try {
        const rendered = await this.request(url.toString(), undefined, { readAttempts: 1 });
        for (const id of missingIds.slice(offset, offset + 5)) {
          if (validSection(id, rendered[id])) sections[id] = rendered[id];
        }
      } catch { /* Retry only missing sections, never a cart mutation. */ }
    }
    for (const id of ids.filter((entry) => !sections[entry])) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', id);
      // Keep the current page and preview context: the header omits its drawer on /cart.
      const html = await this.request(url.toString(), undefined, { format: 'text', readAttempts: 1 });
      if (!validSection(id, html)) throw new Error('Could not refresh the cart section');
      sections[id] = html;
    }
    const cart = await this.request(this.route('cart.js'));
    if (!Array.isArray(cart.items) || !cart.currency) throw new Error('Invalid cart response');
    // A native cart update finished while these requests were in flight. Start with its fresh state.
    if (revision !== this.revision) {
      this.scheduleSync();
      return false;
    }
    const configs = Object.values(sections).flatMap(sectionConfigs);
    if (this.config.enabled !== false && this.config.hasCollectionFilter && ids.length && !configs.length) {
      throw new Error('Could not refresh collection eligibility');
    }
    if (configs.length) this.config = configs[0];
    this.sections = sections;
    const fingerprint = baseFingerprint(cart);
    if (this.fingerprint !== fingerprint) this.giftErrors.clear();
    this.fingerprint = fingerprint;
    this.cart = cart;
    if (this.uncertainMutationSnapshot !== null) {
      this.pendingPublish ||= this.uncertainMutationSnapshot !== JSON.stringify(cart.items);
      this.uncertainMutationSnapshot = null;
    }
    this.unsafe = invalidOwnedLines(cart, this.config).length > 0;
    if (!this.unsafe) {
      this.error = '';
    }
    this.render();
    return true;
  }

  mutationBody(body) {
    return { ...body, sections: this.sectionIds().slice(0, 5), sections_url: window.location.pathname };
  }

  async removeLines(lines) {
    if (!lines.length) return true;
    const keys = new Set(lines.map((line) => line.key));
    this.unsafe = true;
    this.uncertainMutationSnapshot ??= JSON.stringify(this.cart.items);
    const response = await this.request(this.route('cart/update.js'), this.mutationBody({
      updates: Object.fromEntries([...keys].map((key) => [key, 0])),
    }));
    this.pendingPublish = true;
    if (!await this.loadState(response.sections)) return false;
    if (this.cart.items.some((line) => keys.has(line.key))) throw new Error('Gift removal was not applied');
    return true;
  }

  async reconcile() {
    if (this.pendingAddition && !await this.recoverAddition()) return false;
    const rejected = new Set();
    // Removing one gift can change the discount on another. Settle each authoritative response.
    const maximumPasses = (this.cart?.items || []).filter(ownedTier).length + 1;
    for (let pass = 0; pass < maximumPasses; pass += 1) {
      const invalid = invalidOwnedLines(this.cart, this.config);
      if (!invalid.length) {
        this.unsafe = false;
        for (const id of rejected) this.giftErrors.set(id, this.text('notFree'));
        return true;
      }
      const tier = qualifiedTiers(this.cart, this.config)[0];
      // Remove products outside the current entitlement before judging discount reallocations.
      const structural = invalid.filter((item) => !entitledProduct(item, tier));
      const removing = structural.length ? structural : invalid;
      for (const item of removing) {
        if (entitledProduct(item, tier) && Number(item.final_line_price) !== 0) rejected.add(String(item.product_id));
      }
      const before = this.cart.items.filter(ownedTier).length;
      if (!await this.removeLines(removing)) return false;
      if (this.cart.items.filter(ownedTier).length >= before) throw new Error('Gift cleanup did not progress');
    }
    this.unsafe = true;
    throw new Error('Gift cleanup did not settle');
  }

  async postGift(tier, variantId) {
    this.unsafe = true;
    this.uncertainMutationSnapshot ??= JSON.stringify(this.cart.items);
    const cartBefore = JSON.stringify(this.cart.items);
    let response;
    let requestError;
    try {
      response = await this.request(this.route('cart/add.js'), this.mutationBody({
        items: [{ id: variantId, quantity: 1, properties: { [OWN_PROPERTY]: `tier-${tier.id}`, [VERSION_PROPERTY]: '1' } }],
      }));
    } catch (error) {
      requestError = error;
      if (this.pendingAddition) this.pendingAddition.requestError = error;
    }
    // A timed-out request can have succeeded. Read the cart before retrying or rolling back.
    if (!await this.loadState(response?.sections)) return { settled: false, requestError };
    if (JSON.stringify(this.cart.items) !== cartBefore) this.pendingPublish = true;
    return { settled: true, requestError };
  }

  ownedProduct(productId) {
    return (this.cart?.items || []).filter((item) => ownedTier(item) && String(item.product_id) === String(productId));
  }

  async recoverAddition() {
    const pending = this.pendingAddition;
    if (!pending) return true;
    const added = this.ownedProduct(pending.productId);
    const matches = added.length === 1 && String(added[0].variant_id) === pending.variantId;
    if (!pending.rollback && matches && !invalidOwnedLines(this.cart, this.config).length
      && additionIsFree(this.cart, pending)) {
      this.pendingAddition = null;
      this.selections.set(pending.productId, { variantId: pending.variantId, dirty: false });
      if (pending.successError) this.giftErrors.set(pending.productId, pending.successError);
      else this.giftErrors.delete(pending.productId);
      return true;
    }
    const rejection = pending.rejection || this.text(pending.requestError?.status === 422
      ? 'unavailable' : added.length ? 'addRejected' : 'networkError');
    pending.rollback = true;
    pending.rejection = rejection;
    // Preserve the transaction across failed reads/removals so retry cannot delete an older gift first.
    if (added.length && !await this.removeLines(added)) return false;
    this.pendingAddition = null;
    let restored = false;
    // An intervening customer cart mutation cancels restoration of the old option.
    if (pending.previous && this.revision === pending.revision) {
      restored = await this.restoreVariant(pending.previous, rejection, pending.beforeTotal);
      if (restored === null) return false;
    }
    this.giftErrors.set(pending.productId, pending.previous && !restored ? this.text('swapFailed') : rejection);
    return true;
  }

  async restoreVariant(previous, rejection, beforeTotal) {
    const tier = qualifiedTiers(this.cart, this.config)[0];
    const product = tier?.products?.find((entry) => String(entry.id) === String(previous.product_id));
    const variant = product?.variants?.find((entry) => String(entry.id) === String(previous.variant_id) && entry.available);
    if (!variant || this.ownedProduct(product.id).length) return false;
    this.pendingAddition = {
      productId: String(product.id), variantId: String(variant.id), revision: this.revision, successError: rejection,
      beforeTotal,
    };
    const result = await this.postGift(tier, variant.id);
    if (!result.settled) return null;
    const restored = this.ownedProduct(product.id);
    if (restored.length === 1 && String(restored[0].variant_id) === String(variant.id)
      && !invalidOwnedLines(this.cart, this.config).length && additionIsFree(this.cart, this.pendingAddition)) {
      this.pendingAddition = null;
      this.selections.set(String(product.id), { variantId: String(variant.id), dirty: false });
      return true;
    }
    this.pendingAddition.rollback = true;
    this.pendingAddition.rejection = this.text('swapFailed');
    if (restored.length && !await this.removeLines(restored)) return null;
    this.pendingAddition = null;
    return false;
  }

  async addGift(productId, variantId) {
    if (this.designMode()) return;
    let tier = qualifiedTiers(this.cart, this.config)[0];
    let product = tier?.products?.find((entry) => String(entry.id) === String(productId));
    let variant = product?.variants?.find((entry) => String(entry.id) === String(variantId) && entry.available);
    if (!variant) return;
    const previous = this.ownedProduct(productId);
    if (previous.length === 1 && String(previous[0].variant_id) === String(variant.id)
      && previous[0].quantity === 1 && coveredGiftLines(this.cart).has(previous[0])) return;
    const beforeTotal = payableTotal(this.cart);
    // Updating a size changes only this gift. Other selected gifts and paid copies stay in place.
    if (previous.length && !await this.removeLines(previous)) return;
    tier = qualifiedTiers(this.cart, this.config)[0];
    product = tier?.products?.find((entry) => String(entry.id) === String(productId));
    variant = product?.variants?.find((entry) => String(entry.id) === String(variantId) && entry.available);
    this.pendingAddition = { productId: String(productId), variantId: String(variantId),
      previous: previous.length === 1 ? previous[0] : null, revision: this.revision, beforeTotal };
    let result = { settled: true, requestError: { status: 422 } };
    if (variant) result = await this.postGift(tier, variant.id);
    if (!result.settled) return;
    const added = this.ownedProduct(productId);
    const matchesChoice = added.length === 1 && String(added[0].variant_id) === String(variantId);
    if (matchesChoice && !invalidOwnedLines(this.cart, this.config).length
      && additionIsFree(this.cart, this.pendingAddition)) {
      this.pendingAddition = null;
      this.giftErrors.delete(String(productId));
      this.selections.set(String(productId), { variantId: String(variantId), dirty: false });
      this.unsafe = false;
      return;
    }
    const rejection = this.text(result.requestError?.status === 422 ? 'unavailable' : added.length ? 'addRejected' : 'networkError');
    // A new addition must not displace an existing free gift: undo only the new product first.
    this.pendingAddition.rollback = true;
    this.pendingAddition.rejection = rejection;
    if (added.length && !await this.removeLines(added)) return;
    const additionRevision = this.pendingAddition.revision;
    this.pendingAddition = null;
    let restored = false;
    if (previous.length === 1 && this.revision === additionRevision) {
      restored = await this.restoreVariant(previous[0], rejection, beforeTotal);
      if (restored === null) return;
    }
    if (!await this.reconcile()) return;
    this.giftErrors.set(String(productId), previous.length && !restored ? this.text('swapFailed') : rejection);
  }

  publish() {
    if (!this.cart) return;
    const openCart = this.openCartOnPublish;
    this.openCartOnPublish = undefined;
    if (!this.pendingPublish) {
      this.render();
      return;
    }
    this.pendingPublish = false;
    document.dispatchEvent(new CartUpdateEvent(this.cart, SOURCE, {
      source: SOURCE, itemCount: this.cart.item_count, sections: this.sections,
      ...(openCart === false ? { openCart: false } : {}),
    }));
    this.render();
  }

  checkoutTarget(element) {
    if (!(element instanceof Element)) return null;
    return element.closest('[name="checkout"], [href*="/checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart');
  }

  blockCheckout(event) {
    if (!this.config || this.designMode() || (!this.jobs && !this.unsafe && !this.pendingNative && !this.pendingAddition
      && !document.querySelector('cart-items-component.cart-items-disabled'))) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.error = this.text(this.unsafe && !this.jobs ? 'cartSyncError' : 'busy');
    this.render();
    return true;
  }

  handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (this.checkoutTarget(event.submitter) || /\/checkout(?:[/?#]|$)/.test(form.action)
      || (!event.submitter && form.querySelector('[name="checkout"]'))) {
      this.blockCheckout(event);
    } else if (form.closest('product-form-component, cart-discount-component')) {
      if (window.ParaLookBundle?.handles(form)) return;
      this.beginNative();
    }
  }

  selectionFor(product, cart = this.cart) {
    const productId = String(product.id);
    const owned = (cart?.items || []).find((item) => ownedTier(item) && String(item.product_id) === productId);
    let selection = this.selections.get(productId) || { variantId: '', dirty: false };
    if (owned && !selection.dirty) selection = { variantId: String(owned.variant_id), dirty: false };
    const variant = product.variants?.find((entry) => String(entry.id) === selection.variantId);
    selection = {
      ...selection,
      variantId: variant ? String(variant.id) : product.variants?.length === 1 ? String(product.variants[0].id) : '',
    };
    this.selections.set(productId, selection);
    return selection;
  }

  handleChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.closest(ROOT) || !select.matches('[data-variant]')) return;
    if (this.designMode() || this.jobs || this.pendingNative) {
      this.render();
      return;
    }
    const tier = qualifiedTiers(this.cart || this.config?.cart, this.config)[0];
    const productId = select.closest('[data-product-id]')?.dataset.productId;
    const product = tier?.products?.find((entry) => String(entry.id) === productId);
    if (!product || select.closest('[data-tier]')?.dataset.tier !== tier.id) return;
    this.selections.set(productId, { variantId: select.value, dirty: true });
    this.error = '';
    this.giftErrors.delete(productId);
    this.render();
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;
    if (this.checkoutTarget(event.target)) {
      this.blockCheckout(event);
      return;
    }
    const root = event.target.closest(ROOT);
    if (!root) {
      // The theme's remove button starts a request directly, without a quantity event.
      if (event.target.closest('cart-items-component [on\\:click*="onLineItemRemove"], cart-discount-component [on\\:click*="removeDiscount"]')) this.beginNative();
      return;
    }
    const button = event.target.closest('[data-claim], [data-remove], [data-retry]');
    if (!button) return;
    event.preventDefault();
    if (this.designMode() || this.jobs || this.pendingNative) return;
    this.error = '';
    if (button.hasAttribute('data-retry')) {
      this.scheduleSync();
      return;
    }
    const card = button.closest('[data-product-id]');
    const productId = card?.dataset.productId;
    // Match the snapshot used to display the button, even if the initial read failed.
    // This accepts the click only; loadState/reconcile/addGift still verify fresh data before writing.
    const displayedCart = this.cart || this.config?.cart;
    const tier = qualifiedTiers(displayedCart, this.config)[0];
    const product = tier?.products?.find((entry) => String(entry.id) === productId);
    if (!product || card.closest('[data-tier]')?.dataset.tier !== tier.id) return;
    const { variantId } = this.selectionFor(product, displayedCart);
    const removing = button.hasAttribute('data-remove');
    if (!removing && !variantId) {
      this.error = this.text('selectVariant');
      this.render();
      card.querySelector('[data-variant]')?.focus();
      return;
    }
    this.enqueue(async () => {
      if (!await this.loadState() || !await this.reconcile()) return;
      if (removing) {
        if (!await this.removeLines(this.ownedProduct(productId)) || !await this.reconcile()) return;
        this.giftErrors.delete(productId);
      } else {
        await this.addGift(productId, variantId);
      }
      this.publish();
    });
  }

  updateCartControlLocks() {
    // Native quantity requests use line indexes. Freeze those controls while gift mutations can reorder lines.
    if (!this.designMode() && (this.jobs || this.externalMutation)) {
      for (const element of document.querySelectorAll(
        'cart-items-component quantity-selector-component, cart-items-component cart-quantity-selector-component, '
        + 'cart-items-component .cart-items__remove, '
        + 'cart-discount-component, product-form-component [type="submit"], '
        + 'para-complete-look [data-look-add], para-complete-look [data-look-add-selected]'
      )) {
        if (!this.cartControlLocks.has(element)) this.cartControlLocks.set(element, element.inert);
        element.inert = true;
      }
    } else {
      for (const [element, previous] of this.cartControlLocks) element.inert = previous;
      this.cartControlLocks.clear();
    }
  }

  updateAcceleratedCheckoutLocks() {
    const locked = !this.designMode() && Boolean(this.jobs || this.pendingNative || this.unsafe || this.pendingAddition);
    if (locked) {
      for (const element of document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart')) {
        if (!this.checkoutLocks.has(element)) {
          this.checkoutLocks.set(element, { inert: element.inert, pointerEvents: element.style.pointerEvents });
        }
        element.inert = true;
        element.style.pointerEvents = 'none';
      }
    } else {
      for (const [element, previous] of this.checkoutLocks) {
        element.inert = previous.inert;
        element.style.pointerEvents = previous.pointerEvents;
      }
      this.checkoutLocks.clear();
    }
  }

  render() {
    if (!this.config) return;
    const roots = this.roots();
    for (const root of roots) root.pauseObservation?.();
    try {
      this.renderRoots(roots);
    } finally {
      for (const root of roots) root.resumeObservation?.();
    }
  }

  renderSelect(select, options, value) {
    if (!select) return;
    const signature = JSON.stringify(options);
    if (select.dataset.rewardsOptions !== signature) {
      select.replaceChildren(...options.map(({ value: optionValue, label, disabled = false }) => {
        const option = new Option(label, optionValue);
        option.disabled = disabled;
        return option;
      }));
      select.dataset.rewardsOptions = signature;
    }
    select.value = value || '';
  }

  renderChoice(card, product, cart, owned, enabled) {
    const selection = this.selectionFor(product, cart);
    const variant = product.variants?.find((entry) => String(entry.id) === selection.variantId);
    const variantSelect = card.querySelector('[data-variant]');
    this.renderSelect(variantSelect, [
      ...(product.variants?.length !== 1 ? [{ value: '', label: this.text('selectVariant') }] : []),
      ...(product.variants || []).map((entry) => ({ value: String(entry.id),
        label: `${entry.title}${entry.available ? '' : ` — ${this.text('unavailable')}`}`, disabled: !entry.available })),
    ], selection.variantId);
    if (variantSelect) variantSelect.disabled = !enabled;
    const variantWrapper = card.querySelector('[data-variant-wrapper]');
    if (variantWrapper) variantWrapper.hidden = product.variants?.length < 2;
    const sameChoice = owned && String(owned.variant_id) === selection.variantId;
    const claim = card.querySelector('[data-claim]');
    if (claim) {
      claim.textContent = this.text(owned ? 'changeGift' : 'choose');
      claim.hidden = Boolean(sameChoice);
      claim.disabled = !enabled || !variant?.available || Boolean(sameChoice);
    }
    const remove = card.querySelector('[data-remove]');
    if (remove) {
      remove.hidden = !owned;
      remove.disabled = !enabled;
    }
  }

  renderRoots(roots) {
    if (!this.config) return;
    this.updateAcceleratedCheckoutLocks();
    this.updateCartControlLocks();
    const cart = this.cart || this.config.cart || { items: [], currency: this.config.currency };
    const covered = coveredGiftLines(cart);
    const tiers = sortedTiers(this.config);
    const productIds = new Set(tiers.flatMap((tier) => tier.products.map((product) => String(product.id))));
    for (const id of this.selections.keys()) if (!productIds.has(id)) this.selections.delete(id);
    const amount = eligibleAmount(cart, this.config);
    const reached = new Set(qualifiedTiers(cart, this.config).map((tier) => tier.id));
    const maximum = tiers.at(-1)?.amount || 1;
    const next = tiers.find((tier) => tier.amount > amount);
    const busy = Boolean(this.jobs || this.pendingNative);
    const mismatch = cart.currency !== this.config.currency;
    const error = this.error || [...this.giftErrors.values()][0] || '';
    for (const root of roots) {
      const localConfig = parseConfig(root);
      const controllerOnly = localConfig?.surface === 'controller' || root.hasAttribute('data-controller-only');
      root.hidden = Boolean((controllerOnly || (mismatch && !this.designMode())) && !(this.unsafe && error));
      root.dataset.errorOnly = String(Boolean((controllerOnly || mismatch) && this.unsafe && error));
      root.dataset.busy = String(busy);
      root.setAttribute('aria-busy', String(busy));
      const message = root.querySelector('[data-message]');
      if (message) message.textContent = mismatch ? this.text('currencyUnavailable') : next
        ? this.text('nextReward', { amount: this.money(next.amount - amount), reward: next.title }) : this.text('allUnlocked');
      const progress = root.querySelector('[data-progress]');
      if (progress) {
        progress.setAttribute('aria-valuenow', String(Math.round(Math.min(100, amount / maximum * 100))));
        progress.setAttribute('aria-valuetext', this.money(amount));
      }
      const fill = root.querySelector('[data-progress-fill]');
      if (fill) fill.style.width = `${Math.min(100, amount / maximum * 100)}%`;
      for (const [selector, attribute] of [['[data-tier]', 'tier'], ['[data-milestone]', 'milestone']]) {
        const current = [...root.querySelectorAll(selector)];
        const ordered = tiers.map((tier) => current.find((element) => element.dataset[attribute] === tier.id)).filter(Boolean);
        if (ordered.some((element, index) => element !== current[index])) {
          for (const element of ordered) element.parentElement.append(element);
        }
      }
      for (const tier of tiers) {
        const card = root.querySelector(`[data-tier="${tier.id}"]`);
        const milestone = root.querySelector(`[data-milestone="${tier.id}"]`);
        if (milestone) {
          milestone.dataset.reached = String(amount >= tier.amount);
          milestone.dataset.unlocked = String(amount >= tier.amount);
        }
        if (!card) continue;
        const active = reached.has(tier.id);
        const available = tier.products.some((product) => product.variants?.some((variant) => variant.available)
          || (active && cart.items.some((item) => ownedTier(item) && String(item.product_id) === String(product.id)
            && covered.has(item))));
        const tierState = active ? (available ? 'available' : 'unavailable') : amount >= tier.amount ? 'superseded' : 'locked';
        card.dataset.state = tierState;
        const tierStatus = card.querySelector('[data-tier-status]');
        if (tierStatus) tierStatus.textContent = this.text(tierState);
        for (const product of tier.products) {
          const productCard = card.querySelector(`[data-product-id="${product.id}"]`);
          if (!productCard) continue;
          const owned = active && cart.items.find((item) => ownedTier(item)
            && String(item.product_id) === String(product.id) && covered.has(item));
          const productAvailable = product.variants?.some((variant) => variant.available);
          const state = !active ? tierState : owned ? 'added' : productAvailable ? 'available' : 'unavailable';
          productCard.dataset.state = state;
          const status = productCard.querySelector('[data-status]');
          if (status) status.textContent = this.text(state);
          this.renderChoice(productCard, product, cart, owned, !busy && active && !this.designMode());
        }
      }
      const errorElement = root.querySelector('[data-error]');
      if (errorElement) {
        errorElement.hidden = !error;
        errorElement.textContent = error;
      }
      const retry = root.querySelector('[data-retry]');
      if (retry) {
        retry.hidden = !this.error || busy;
        retry.disabled = busy || this.designMode();
      }
    }
  }
}

const controller = new RewardsController();
class ParaCartRewards extends HTMLElement {
  observer = new MutationObserver(() => controller.register(true));

  connectedCallback() {
    this.resumeObservation();
    controller.register(true);
  }

  disconnectedCallback() { this.pauseObservation(); }

  pauseObservation() { this.observer.disconnect(); }

  resumeObservation() {
    if (!this.isConnected) return;
    // The theme morphs existing custom elements without reconnecting them. Watch only this
    // rewards subtree, and suspend observation for our own rendering to prevent feedback loops.
    this.observer.observe(this, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['disabled', 'hidden'],
    });
  }
}
if (!customElements.get(ROOT)) customElements.define(ROOT, ParaCartRewards);
